import type { ContractLifecycleView } from "@closer/shared";
import { useQuery } from "@tanstack/react-query";

import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { ContractApprovalSection } from "./ContractApprovalSection";
import { useContractDetail } from "./contractDetailContext";
import { ContractLifecycleStrip } from "./ContractLifecycleStrip";
import { ContractReconciliationSection } from "./ContractReconciliationSection";
import { ContractReviewSection } from "./ContractReviewSection";
import { ContractSignatureSection } from "./ContractSignatureSection";

/**
 * SÜREÇ bölümü — sözleşmeyi ilerleten dört akış.
 *
 * ### ⚠ SIRA KANONİK ve görünür olmalı
 *
 * İnceleme → iç onay → taraf mutabakatı → imza. Dördü eşit ağırlıkta kart olarak yan yana
 * duruyordu ve her biri ayrı ayrı "henüz başlatılmadı" diyordu; bir SIRA olduğu
 * görünmediği için kullanıcı hiçbirine dokunmuyordu (canlı tur, 4 Eylül). Sıranın kendisi
 * kabuktaki yaşam döngüsü şeridinde çiziliyor — o şerit bilerek bölümlerin DIŞINDA, çünkü
 * sözleşmenin hangi aşamada olduğu bir bölümün detayı değil, kaydın kimliği.
 *
 * ### ⚠ ARŞİVDE AKIŞ BAŞLAMIYOR
 *
 * Kusur avı (26 Ağustos): sunucu dördünü de reddediyor. Düğmeyi çizmek kullanıcıyı formu
 * doldurup 409 yemeye göndermekti. Yürüyen akışın komutları KALIYOR — arşivleme onları
 * bitirmiyor. Kural bölümlerin kendi içinde (`arsivde`), burada değil: her akışın
 * arşivdeki davranışı aynı değil.
 *
 * ### ⚠ TAM YAŞAM DÖNGÜSÜ ŞERİDİ BURADA
 *
 * Şerit kabuktaydı ve her bölümün üstünde ~65px yiyordu (5 Eylül ölçümü: içerik 429.
 * pikselde başlıyordu, hedef tasarımda 258). Kabukta yalnız kompakt çip kaldı
 * ("Aşama 7/10") — 4 Eylül turunun şartını, yani aşamanın her sekmede görünmesini,
 * karşılamaya devam ediyor.
 *
 * Adımların SIRASININ okunması gereken tek yer burası: dört akış kartı zaten bu sayfada
 * ve şerit onların hangi düzende yürüdüğünü söylüyor. Kartların hemen üstünde durması,
 * kabuğun herhangi bir yerinde durmasından daha çok şey anlatıyor.
 *
 * ### ⚠ AI burada DEĞİL
 *
 * Analiz bir KARAR halkası değil, bir OKUMA yardımı (KARAR 11: *"kendiliğinden
 * versiyon/karar doğurmaz"*). Bu dörtlünün arasına koysaydık ekran onu zincirin bir adımı
 * gibi gösterirdi; oysa hiçbir akışı başlatmıyor ve hiçbirini kapatmıyor. Belge bölümünde.
 */
export function ContractProcessPage() {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const { id, arsivde, kayitYazma } = useContractDetail();

  /** ⚠ Anahtar KABUKTAKİYLE AYNI: kabuk aşama çipi için zaten okuyor, ek istek yok. */
  const yasam = useQuery({
    queryKey: tenantKey(tenantId, "contract", id, "yasam"),
    queryFn: async () =>
      (await api<ContractLifecycleView>(`/api/contracts/${id}/lifecycle`, { tenantId })).data,
  });

  return (
    <div className="flex flex-col gap-4">
      {yasam.data ? (
        <ContractLifecycleStrip states={yasam.data.states} status={yasam.data.status} />
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ContractReviewSection contractId={id} arsivde={arsivde} kayitYazma={kayitYazma} />
        <ContractApprovalSection contractId={id} arsivde={arsivde} />
        <ContractReconciliationSection contractId={id} arsivde={arsivde} />
        <ContractSignatureSection contractId={id} arsivde={arsivde} />
      </div>
    </div>
  );
}
